import classNames from "classnames";
import React, { useRef } from "react";
import type { BackendNotificationTarget } from "../../../shared/utility/mcp-backend-notifications";
import { useRevealNotifications } from "../../../shared/utility/use-reveal-notifications";
import KuikaRevealTooltip from "../../ui-kit/kuika-reveal-tooltip/kuika-reveal-tooltip";
import "./left-menu-reveal-icon.scss";

/**
 * Wraps a left-menu icon button so it lights up (purple glow) and shows a
 * right-anchored reveal tooltip when the AI runs a backend tool that touches
 * its surface — the left-menu mirror of the top-toolbar reveal.
 *
 * Subscription/shimmer/auto-clear logic is centralized in `useRevealNotifications`
 * so each of the four wired buttons (datasources / language / scheduled-jobs /
 * api-management) shares one implementation instead of duplicating it.
 */
interface ILeftMenuRevealIconProps {
  /** Notification targets that should light up this icon (stable reference). */
  targets: readonly BackendNotificationTarget[];
  children: React.ReactNode;
}

const LeftMenuRevealIcon: React.FC<ILeftMenuRevealIconProps> = ({ targets, children }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const { notifications, shimmer, shimmerDelete } = useRevealNotifications(targets);

  return (
    <div
      ref={anchorRef}
      className={classNames("kuika-left-menu-reveal", {
        "kuika-left-menu-reveal--shimmer": shimmer && !shimmerDelete,
        "kuika-left-menu-reveal--shimmer-delete": shimmerDelete
      })}
    >
      {children}
      {notifications.length > 0 && (
        <KuikaRevealTooltip anchorRef={anchorRef} notifications={notifications} placement="right" />
      )}
    </div>
  );
};

export default LeftMenuRevealIcon;
